import {
  UseMutationOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import { AxiosError } from "axios"

import { AdminRequests, AdminResponses } from "@tfg/types/endpoints/admin"
import { PaginationType } from "@tfg/types/schemas/utils"

import { httpClient } from "~/backend/axios"
import { queryClient } from "~/backend/queryClient"
import { syncOrgCustomFieldsToProducts } from "~/gen/api/hooks/useSyncOrgCustomFieldsToProducts"
import type { SyncOrgCustomFieldsToProductsQueryParams } from "~/gen/api/types/SyncOrgCustomFieldsToProducts"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const useListCustomFieldsQuery = (orgId?: string) =>
  useQuery({
    queryKey: ["customFields", orgId],

    queryFn: async () => {
      const res = await httpClient.get<AdminResponses.CustomFieldDefinition[]>(
        `/admin/custom-fields`,
        {
          params: {
            orgId,
          },
        }
      )
      return res.data
    },
  })

export const useCreateCustomFieldMutation = (
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    AdminRequests.CustomFieldDefinitionCreate
  >
) =>
  useMutation({
    mutationFn: async (
      customField: AdminRequests.CustomFieldDefinitionCreate
    ) => {
      return await httpClient.post(`/admin/custom-fields`, customField)
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)

      showSuccessNotification({
        title: "Success",
        message: "Custom field definition created",
      })

      queryClient.invalidateQueries({ queryKey: ["customFields"] })
    },
    onError: (error: AxiosError) => {
      showAxiosError({
        notificationTitle: "Error",
        notificationMessage: error.message,
        showNotification: true,
        error,
      })
    },
  })

export const useDeleteCustomFieldMutation = (
  options?: UseMutationOptions<void, AxiosError, unknown>
) => {
  return useMutation({
    ...options,
    mutationFn: async (customFieldId) => {
      return httpClient.delete(`/admin/custom-fields/${customFieldId}`)
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      queryClient.invalidateQueries({ queryKey: ["customFields"] })
    },
  })
}

export const useListTextContentsQuery = ({
  pagination,
  searchParams,
}: PaginationType) =>
  useQuery({
    queryKey: ["textContents", searchParams],

    queryFn: async () => {
      const res = await httpClient.get<AdminResponses.TextContent[]>(
        "/admin/text-contents",
        {
          params: {
            pagination,
            searchParams,
          },
        }
      )
      return res.data
    },
  })

export const useCreateTextContentMutation = (
  options?: UseMutationOptions<
    AdminResponses.TextContentWithouTranslation,
    unknown,
    { originalText: string }
  >
) => {
  return useMutation({
    ...options,
    mutationFn: async ({ originalText }) => {
      const res = await httpClient.post("/admin/text-contents", {
        originalText,
      })
      return res.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      queryClient.invalidateQueries({ queryKey: ["textContents"] })
      showSuccessNotification({
        title: "Added",
        message: "Successfully created text content",
      })
    },
  })
}

export const useSyncCustomFieldsToProductsMutation = (
  params: SyncOrgCustomFieldsToProductsQueryParams,
  options?: UseMutationOptions<unknown, unknown, unknown>
) =>
  useMutation({
    mutationFn: async () => {
      const res = await syncOrgCustomFieldsToProducts(params)
      return res.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      queryClient.invalidateQueries({ queryKey: ["customFields"] })
    },
  })

export const useUpdateCustomFieldMutation = (
  { id }: { id: number | undefined },
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    AdminRequests.CustomFieldDefinitionCreate
  >
) =>
  useMutation({
    mutationFn: async (
      customField: AdminRequests.CustomFieldDefinitionCreate
    ) => {
      if (!id) {
        throw new Error("Custom field id is required")
      }

      return await httpClient.put(`/admin/custom-fields/${id}`, customField)
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      showSuccessNotification({
        title: "Success",
        message: "Custom field definition updated",
      })
      queryClient.invalidateQueries({ queryKey: ["customFields"] })
    },
    onError: (error: AxiosError) => {
      showAxiosError({
        error,
        showNotification: true,
        notificationTitle: "Error",
        defaultMessage: "Error updating custom field",
      })
    },
  })
